import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import type { Feedback } from '@vuokramani/shared';
import { Button } from '../../components/ui.tsx';
import { api } from '../../lib/api.ts';
import s from './admin.module.css';

const TYPE_LABEL: Record<Feedback['type'], string> = {
  bug: 'Virhe',
  feedback: 'Palaute',
  question: 'Kysymys',
};

const fmt = new Intl.DateTimeFormat('fi-FI', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Helsinki',
});

/**
 * Apua-painikkeen viestit. Bugi-ilmoitukset-välilehti näyttää virheet, Palautteet muut.
 * Uudet ensin; käsitelty-merkintä siirtää viestin listan loppuun.
 */
export function FeedbackAdmin({ types }: { types: Array<Feedback['type']> }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'feedback'],
    queryFn: () => api<{ feedback: Feedback[] }>('/admin/feedback').then((r) => r.feedback),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Feedback['status'] }) =>
      api<void>(`/admin/feedback/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });

  const items = (data ?? []).filter((f) => types.includes(f.type));
  if (isLoading) return null;
  if (items.length === 0) return <p className={s.feedbackEmpty}>Ei viestejä.</p>;

  return (
    <ul className={s.feedbackList}>
      {items.map((f) => (
        <li key={f.id} className={`${s.feedbackItem} ${f.status === 'done' ? s.feedbackDone : ''}`}>
          <div className={s.feedbackHead}>
            <span className={s.feedbackType}>{TYPE_LABEL[f.type]}</span>
            <span>{fmt.format(new Date(f.createdAt))}</span>
            <span>{f.userName ?? f.userEmail ?? 'Poistettu käyttäjä'}</span>
            {f.userName && f.userEmail && <a href={`mailto:${f.userEmail}`}>{f.userEmail}</a>}
            {f.page && <code>{f.page}</code>}
          </div>
          <p className={s.feedbackMessage}>{f.message}</p>
          <div>
            {f.status === 'new' ? (
              <Button
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: f.id, status: 'done' })}
              >
                <CheckCircle2 size={15} aria-hidden="true" />
                Merkitse käsitellyksi
              </Button>
            ) : (
              <Button
                variant="ghost"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: f.id, status: 'new' })}
              >
                <RotateCcw size={15} aria-hidden="true" />
                Palauta uudeksi
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
